import { useQuery } from "@tanstack/react-query";
import { getScopeOptions, ScopeOptionsQuery } from "../api/rolesPermissionsApi";
import type { ScopeOptionPage } from "../types/RolesPermissions.type";

export const SCOPE_OPTIONS_KEY = "rbac-scope-options";

const EMPTY_PAGE: ScopeOptionPage = {
	data: [],
	currentPage: 1,
	perPage: 10,
	total: 0,
	lastPage: 1,
	truncated: false,
};

// GET /api/v2/rbac/scopes (CONTRACT §3). Server-side search + pagination (10/page) + cascade
// breadcrumb + exclusion + already-granted. The catalogue is deliberately NOT filtered by the
// granter's C13 ceiling — the ceiling clamps at write time server-side — so the UI must not
// hide options client-side either. `page`/`exclude`/`userId`/`roleId` are all part of the query
// key: any change to the exclusion set (select/deselect a higher-order item) must reset the
// list back to page 1 and refetch (§3.4) — a plain key change achieves that for free via
// react-query's normal cache-miss behaviour.
export const useScopeOptions = (query: ScopeOptionsQuery = {}, enabled = true) => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: [SCOPE_OPTIONS_KEY, query],
		queryFn: () => getScopeOptions(query),
		enabled,
	});

	// PRO-757 A3 — `refetch` is exposed so a list that failed can offer a retry, the same
	// affordance `BulkUserPickerModal` already gives its own failed page load. Additive:
	// existing callers destructure what they need.
	return { scopeOptionsPage: data ?? EMPTY_PAGE, isLoading, error, refetch };
};
